import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getLeaveTypeColor } from '../utils/leaveTypeColors';

export interface LeaveTypePieChartEntry {
  leaveTypeName: string;
  daysTaken: number;
}

interface LeaveTypePieChartProps {
  /** Entries with leaveTypeName and daysTaken (can be per-row; we aggregate by type). */
  entries: LeaveTypePieChartEntry[];
  height?: number;
  title?: string;
}

/**
 * Pie chart: total days taken per leave type across the filtered dataset.
 */
export default function LeaveTypePieChart({
  entries,
  height = 320,
  title = 'Leave days by type',
}: LeaveTypePieChartProps) {
  const pieData = useMemo(() => {
    const byType = new Map<string, number>();
    for (const e of entries) {
      if (!e.daysTaken) continue;
      const current = byType.get(e.leaveTypeName) ?? 0;
      byType.set(e.leaveTypeName, current + e.daysTaken);
    }
    return Array.from(byType.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [entries]);

  if (pieData.length === 0) {
    return null;
  }

  const totalDays = pieData.reduce((s, d) => s + d.value, 0);

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ width: '100%', minHeight: height, height: `${height}px`, overflow: 'hidden' }}>
        <ResponsiveContainer width="100%" height={height}>
          <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="75%"
              label={({ name, value }) => `${name}: ${value}`}
              labelLine
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getLeaveTypeColor(entry.name)} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => {
                const pct = totalDays ? ((value / totalDays) * 100).toFixed(1) : '0';
                return [`${value} days (${pct}%)`, name];
              }}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
